import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, api, brl, formatApiError, fmtExpected, shopLogoUrl, STATUS_META } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { waLink } from "@/components/OrderQrCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, MessageCircle, Phone, Check, CalendarClock, ShieldCheck, History, Store, Copy, Clipboard } from "lucide-react";
import { toast } from "sonner";
import { CarDiagram, ZONES, DMG } from "@/components/InspectionCard";
import { OilSummary } from "@/components/OilChangeCard";
import { TechChecklistView } from "@/components/TechChecklist";

const STEPS = ["parado", "aguardando_aprovacao", "em_andamento", "pronto", "entregue"];
const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString("pt-BR") : "");

function ShopHeader({ shop }) {
  return (
    <div className="text-center">
      {shop.logo_path ? <img src={shopLogoUrl(shop.id)} alt={shop.name} className="mx-auto h-16 w-auto" data-testid="track-shop-logo" />
        : <div className="inline-flex items-center gap-2"><Store className="h-6 w-6" style={{ color: "var(--c-brand)" }} /><span className="font-display text-2xl font-black uppercase text-zinc-100">{shop.name}</span></div>}
      {shop.logo_path && <div className="font-display font-bold text-zinc-100 mt-2">{shop.name}</div>}
    </div>
  );
}

function Gate({ token, plate, onOk }) {
  const [v, setV] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post(`/public/track/${token}/unlock`, { last4: v }); sessionStorage.setItem(`trk-${token}`, data.view_token); onOk(data.view_token); }
    catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="track-gate" className="rounded-2xl border border-card2-border bg-card2 p-6 space-y-4">
      <div className="flex items-center gap-2 text-zinc-300"><Lock className="h-4 w-4" style={{ color: "var(--c-brand)" }} /> Acompanhamento do veículo <b className="font-mono">{plate}</b></div>
      <p className="text-sm text-zinc-400">Para proteger seus dados, digite os <b>4 últimos dígitos do telefone</b> cadastrado na oficina.</p>
      <Input data-testid="track-last4-input" inputMode="numeric" maxLength={4} value={v} onChange={(e) => setV(e.target.value.replace(/\D/g, ""))} className="h-12 text-center text-2xl tracking-[0.5em] bg-zinc-900 border-zinc-800" style={{ fontSize: 24 }} placeholder="0000" />
      {err && <div data-testid="track-gate-error" className="text-sm text-red-400">{err}</div>}
      <Button data-testid="track-unlock-btn" disabled={busy || v.length !== 4} className="w-full h-12 font-bold uppercase" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ver minha OS"}</Button>
    </form>
  );
}

function Steps({ status }) {
  const idx = STEPS.indexOf(status);
  if (idx < 0) return <div className="text-sm text-red-300">{STATUS_META[status]?.label || status}</div>;
  return (
    <ol className="space-y-2" data-testid="track-steps">
      {STEPS.map((s, i) => (
        <li key={s} className={`flex items-center gap-3 text-sm ${i <= idx ? "text-zinc-100" : "text-zinc-600"}`}>
          <span className={`h-6 w-6 rounded-full flex items-center justify-center border ${i < idx ? "border-transparent" : i === idx ? "border-2" : "border-zinc-700"}`} style={i <= idx ? { background: i < idx ? "var(--c-brand)" : "transparent", borderColor: "var(--c-brand)" } : {}}>
            {i < idx ? <Check className="h-3.5 w-3.5" style={{ color: "var(--c-brand-fg)" }} /> : i === idx ? <span className="h-2 w-2 rounded-full" style={{ background: "var(--c-brand)" }} /> : null}
          </span>
          <span className={i === idx ? "font-bold" : ""}>{s === "entregue" ? "Entregue" : STATUS_META[s]?.label}</span>
        </li>
      ))}
    </ol>
  );
}

const Box = ({ title, icon: Icon, children, testid }) => (
  <section data-testid={testid} className="rounded-2xl border border-card2-border bg-card2 p-5">
    <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-zinc-100 mb-3">{Icon && <Icon className="h-4 w-4" style={{ color: "var(--c-brand)" }} />}{title}</h2>
    {children}
  </section>
);

function BudgetLine({ it, i, hidden }) {
  return (
    <li className="py-2.5" data-testid={`track-budget-item-${i}`}>
      <div className="flex justify-between gap-3 text-sm">
        <span className="text-zinc-100">{hidden ? <><span className="text-zinc-400">{Number(it.quantity || 0).toLocaleString("pt-BR")}x </span>{it.description}</> : it.description}</span>
        <span className="text-zinc-300 whitespace-nowrap">{hidden ? "—" : brl(Number(it.quantity) * Number(it.unit_price) - (Number(it.discount) || 0))}</span>
      </div>
      {it.explanation && <p className="mt-1 text-xs text-zinc-400 leading-relaxed" data-testid={`track-budget-expl-${i}`}>{it.explanation}</p>}
    </li>
  );
}

function Budget({ b, slug }) {
  const grp = !!b.group_labor;
  const idx = b.items.map((it, i) => ({ it, i }));
  const parts = grp ? idx.filter((x) => x.it.kind === "p") : idx;
  const servs = grp ? idx.filter((x) => x.it.kind !== "p") : [];
  return (
    <Box title="Orçamento" testid="track-budget">
      <ul className="divide-y divide-zinc-800">
        {parts.map(({ it, i }) => <BudgetLine key={i} it={it} i={i} />)}
        {servs.map(({ it, i }) => <BudgetLine key={i} it={it} i={i} hidden />)}
        {servs.length > 0 && (
          <li className="py-2.5 flex justify-between gap-3 text-sm font-bold" data-testid="track-budget-labor-total">
            <span className="text-zinc-100">Mão de obra dos itens acima</span><span className="text-zinc-100 whitespace-nowrap">{brl(b.labor_total)}</span>
          </li>
        )}
      </ul>
      <div className="flex justify-between pt-3 border-t border-zinc-700"><span className="text-xs text-zinc-400 uppercase">Total</span><span className="font-display text-2xl font-black" style={{ color: "var(--c-brand-neon)" }}>{brl(b.total)}</span></div>
      {b.decision === "pendente" && <a data-testid="track-approve-link" href={slug ? `/o/${slug}` : "/login"} className="mt-3 block text-center h-11 leading-[2.75rem] rounded-xl font-bold uppercase text-sm" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}>Entrar para aprovar</a>}
      {b.decision !== "pendente" && <div className="mt-2 text-sm text-zinc-400">Orçamento {b.decision}.</div>}
    </Box>
  );
}

function PixBox({ pix, code }) {
  const copy = async (v, msg) => { try { await navigator.clipboard.writeText(v); toast.success(msg); } catch { window.prompt("Copie:", v); } };
  return (
    <Box title="Pague com Pix" testid="track-pix">
      <div className="text-sm text-zinc-300">Chave: <b className="break-all">{pix.key}</b></div>
      <div className="text-xs text-zinc-500">Favorecido: {pix.name}</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button data-testid="track-pix-copy-key" onClick={() => copy(pix.key, "Chave Pix copiada")} className="h-11 rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-100 font-bold text-sm flex items-center justify-center gap-2"><Copy className="h-4 w-4" /> Copiar chave Pix</button>
        {code && <button data-testid="track-pix-copy-code" onClick={() => copy(code, "Pix copia e cola copiado")} className="h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2" style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}><Copy className="h-4 w-4" /> Copiar Pix copia e cola</button>}
      </div>
    </Box>
  );
}

export default function PublicTrack() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(() => sessionStorage.getItem(`trk-${token}`));
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/public/track/${token}`).then(({ data: d }) => setMeta(d)).catch(() => setMeta(false)); }, [token]);
  useEffect(() => {
    applyBrandColor(meta?.shop?.brand_color || DEFAULT_BRAND_COLOR);
    if (meta?.shop?.name) document.title = `${meta.shop.name} — Minha OS`;
    return () => applyBrandColor(DEFAULT_BRAND_COLOR);
  }, [meta]);
  useEffect(() => {
    if (!view) { return; }
    api.get(`/public/track/${token}/data`, { params: { v: view } }).then(({ data: d }) => setData(d)).catch(() => { sessionStorage.removeItem(`trk-${token}`); setView(null); });
  }, [view, token]);
  if (meta === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  if (meta === false) return <div data-testid="track-invalid" className="min-h-screen flex items-center justify-center p-6 text-center text-zinc-400">Link inválido ou substituído por um novo. Peça o link atualizado à oficina.</div>;
  const shop = meta.shop;
  const o = data?.order;
  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-xl mx-auto space-y-4">
        <ShopHeader shop={shop} />
        {!data ? <Gate token={token} plate={meta.plate} onOk={setView} /> : (
          <>
            <Box title={[o.order_number && `OS #${o.order_number}`, o.vehicle.plate].filter(Boolean).join(" · ")} testid="track-status">
              <div className="text-sm text-zinc-400 mb-4">{[o.vehicle.make, o.vehicle.model, [o.vehicle.year, o.vehicle.year_model].filter(Boolean).join("/")].filter(Boolean).join(" ")} · entrada {fmtDate(o.created_at)}</div>
              <Steps status={o.status} />
              {o.expected_at && !["pronto", "entregue"].includes(o.status) && <div data-testid="track-expected" className="mt-4 text-sm text-zinc-200"><CalendarClock className="inline h-4 w-4 mr-1" /> Previsão de entrega: <b>{fmtExpected(o.expected_at)}</b></div>}
            </Box>
            {o.tech_report && (() => {
              const tr = o.tech_report;
              const has = (tr.diagnosis || "").trim() || (tr.cause || "").trim() || (tr.solution || "").trim() || (data.laudo_photos || []).length > 0;
              if (!has) return null;
              return (
                <Box title="Laudo técnico" icon={Clipboard} testid="track-laudo">
                  {[["Diagnóstico", tr.diagnosis], ["Causa provável", tr.cause], ["Solução recomendada", tr.solution]].map(([lbl, v]) => (
                    (v || "").trim() ? (
                      <div key={lbl} data-testid={`track-laudo-${lbl}`} className="mb-2">
                        <div className="text-[11px] font-mono-caps text-zinc-500">{lbl}</div>
                        <div className="whitespace-pre-wrap text-sm text-zinc-100">{v}</div>
                      </div>
                    ) : null
                  ))}
                  {tr.mechanic_name && <div className="text-xs text-zinc-400">Mecânico: <b>{tr.mechanic_name}</b></div>}
                  {(data.laudo_photos || []).length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2" data-testid="track-laudo-photos">
                      {data.laudo_photos.map((p) => (
                        <a key={p.id} href={`${API}/public/track/${token}/photo/${p.id}?v=${view}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${API}/public/track/${token}/photo/${p.id}?v=${view}`} alt="" loading="lazy" className="aspect-square w-full object-cover rounded-lg border border-zinc-800" />
                        </a>
                      ))}
                    </div>
                  )}
                </Box>
              );
            })()}
            {o.budget && <Budget b={o.budget} slug={shop.slug} />}
            {data.shop?.pix?.key && <PixBox pix={data.shop.pix} code={o.pix_copy_paste} />}
            {data.photos.length > 0 && (
              <Box title="Fotos" testid="track-photos">
                <div className="grid grid-cols-3 gap-2">{data.photos.map((p) => (
                  <a key={p.id} href={`${API}/public/track/${token}/photo/${p.id}?v=${view}`} target="_blank" rel="noopener noreferrer"><img src={`${API}/public/track/${token}/photo/${p.id}?v=${view}`} alt="" loading="lazy" className="aspect-square w-full object-cover rounded-lg border border-zinc-800" /></a>
                ))}</div>
              </Box>
            )}
            {(o.next_revision?.date || o.next_revision?.km || o.warranty_days || o.warranty_km) && (
              <Box title="Revisão e garantia" icon={ShieldCheck} testid="track-revision">
                {(o.next_revision?.date || o.next_revision?.km) && <div className="text-sm text-zinc-200">Próxima revisão: <b>{[o.next_revision.date && fmtDate(o.next_revision.date), o.next_revision.km && `${Number(o.next_revision.km).toLocaleString("pt-BR")} km`].filter(Boolean).join(" ou ")}</b></div>}
                {(o.warranty_days || o.warranty_km) && <div className="text-sm text-zinc-200 mt-1">Garantia: <b>{[o.warranty_days && `${o.warranty_days} dias`, o.warranty_km && `${Number(o.warranty_km).toLocaleString("pt-BR")} km`].filter(Boolean).join(" / ")}</b></div>}
              </Box>
            )}
            {o.oil_change && (
              <Box title="Troca de óleo" testid="track-oil"><OilSummary oil={o.oil_change} /></Box>
            )}
            {o.inspection && (
              <Box title="Vistoria de entrada" testid="track-inspection">
                <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                  <CarDiagram damages={o.inspection.damages || []} />
                  <div className="text-sm text-zinc-300 space-y-1">
                    {o.inspection.km != null && <div>KM: <b>{Number(o.inspection.km).toLocaleString("pt-BR")}</b></div>}
                    {o.inspection.fuel && <div>Combustível: <b>{o.inspection.fuel}</b></div>}
                    {(o.inspection.damages || []).map((d, i) => <div key={i} className="text-xs">{i + 1}. {ZONES.find((z) => z.k === d.zone)?.l} · {DMG[d.type]}{d.note ? ` — ${d.note}` : ""}</div>)}
                    {Object.keys(o.inspection.items || {}).length > 0 && <div className="text-xs">Itens: {Object.entries(o.inspection.items).map(([k, v]) => `${k} ${v ? "✓" : "✗"}`).join(", ")}</div>}
                    {o.inspection_signed && <div className="text-xs text-green-300">Assinada pelo cliente</div>}
                  </div>
                </div>
              </Box>
            )}
            {o.tech_checklist?.length > 0 && (
              <Box title="Checklist completo (inspeção técnica)" testid="track-tech-checklist"><TechChecklistView entries={o.tech_checklist} /></Box>
            )}
            {data.history.length > 0 && (
              <Box title="Histórico do veículo" icon={History} testid="track-history">
                <ul className="space-y-2">{data.history.map((h, i) => (
                  <li key={i} className="text-sm border-l-2 border-zinc-700 pl-3"><div className="text-zinc-200">{fmtDate(h.created_at)} · OS #{h.order_number}{h.km ? ` · ${Number(h.km).toLocaleString("pt-BR")} km` : ""}</div><div className="text-zinc-400 text-xs">{h.description}</div></li>
                ))}</ul>
              </Box>
            )}
          </>
        )}
        {shop.phone && (
          <div className="grid grid-cols-2 gap-2">
            <a data-testid="track-wa-btn" href={waLink(shop.whatsapp, `Olá! Sobre a OS do veículo ${meta.plate}.`)} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <a data-testid="track-call-btn" href={`tel:${shop.phone}`} className="h-12 rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-100 font-bold flex items-center justify-center gap-2"><Phone className="h-4 w-4" /> Ligar</a>
          </div>
        )}
        <p className="text-center text-[11px] font-mono-caps text-zinc-600 tracking-widest pt-4">feito com AutoOficina</p>
      </div>
    </div>
  );
}
